'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import './reveal-safety.css'

/**
 * The reference shell's behaviours (public/pixelmojo-rebuild/pixelmojo-home.html
 * inline script), ported as one client component for the marketing shell:
 * scroll reveals, hero word reveal, count-up, scroll-spy rail and the nav
 * band that adapts to the sheet beneath it. Not ported (Lloyd, 2026-10-09):
 * the magnetic buttons, the custom cursor ring (the native pointer stays) and
 * the page-settings pills; the OS reduced-motion setting is honoured.
 *
 * Everything it adds to the page (grain, rail) renders only
 * after mount, so the server HTML a crawler reads is unchanged. Reveal motion
 * is opt-in: nothing is hidden until html[data-reveal-ready] is set here, and
 * that never happens under reduced motion.
 *
 * It runs from the (app) layout and writes to page markup (data-in, --i), so
 * marketing routes have no loading.tsx: its Suspense boundary hydrates the
 * page after this effect has already written there, and React reports a
 * hydration mismatch (blogs, hive, projects and services did until
 * 2026-10-09, with old-design skeletons).
 */

type RailItem = { id: string; label: string }

// The same predicate RefScript and HeroSequence use: body.reduce-motion is
// the reference's page-settings toggle (not ported), honoured if it returns.
const reducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
  document.body.classList.contains('reduce-motion')

/**
 * Hero headline words reveal by a CSS animation (src/app/hero-title.css), so
 * a headline that mounts after the route effect ran (client navigation)
 * still reveals itself. This only sets each word's stagger index; without it
 * the words simply rise together.
 */
const indexHeroWords = () =>
  document.querySelectorAll<HTMLElement>('.shell h1').forEach(h1 =>
    h1.querySelectorAll<HTMLElement>('.word > span').forEach((s, i) => {
      // The observer runs on every DOM change; write only what differs.
      if (s.style.getPropertyValue('--i') !== String(i))
        s.style.setProperty('--i', String(i))
    })
  )

export default function ShellEffects() {
  const pathname = usePathname()
  const [mounted, setMounted] = useState(false)
  const [rail, setRail] = useState<RailItem[]>([])
  const [railActive, setRailActive] = useState(-1)
  const [railDark, setRailDark] = useState(false)

  // Mounted inside ConditionalMarketingShell, so never on /platform/app or the
  // Sites control plane. A client navigation from a marketing page into one
  // unmounts it; clear the two page-wide flags it set, so nothing of the
  // marketing shell carries into the app (they are read only under .shell).
  useEffect(() => {
    setMounted(true)
    return () => {
      document.documentElement.removeAttribute('data-reveal-ready')
      delete document.body.dataset.under
    }
  }, [])

  // Reveals, hero words, count-up, magnetic, rail and nav band: re-armed on
  // every client-side navigation.
  useEffect(() => {
    const root = document.documentElement
    const cleanups: Array<() => void> = []
    const reduced = reducedMotion()
    // Before the reveal flag goes up, so the first frame already staggers.
    indexHeroWords()
    const inView = (el: Element) => {
      const r = el.getBoundingClientRect()
      return r.top < window.innerHeight && r.bottom > 0
    }

    // Scroll reveals. The targets are re-queried on every check rather than
    // captured once: a client component can replace its server-rendered node
    // during hydration, and a node captured before that would never reveal.
    // Elements already on screen are marked before the hidden state is
    // enabled, so nothing flashes out. A reveal starts as soon as any part of
    // it enters the viewport (PIX-172; it used to wait for the top 90%).
    const REVEAL =
      '.shell [data-reveal]:not([data-in]), .shell .reveal-stagger:not([data-in]), .shell [data-observe]:not([data-in]), .shell #gauge:not([data-in]), .shell #leader:not([data-in])'
    const revealInView = () =>
      document.querySelectorAll<HTMLElement>(REVEAL).forEach(el => {
        if (inView(el)) el.setAttribute('data-in', '')
      })
    if (reduced) {
      document
        .querySelectorAll(REVEAL)
        .forEach(el => el.setAttribute('data-in', ''))
      root.removeAttribute('data-reveal-ready')
    } else {
      document
        .querySelectorAll(REVEAL)
        .forEach(el => inView(el) && el.setAttribute('data-in', ''))
      root.setAttribute('data-reveal-ready', '')
      let frame = 0
      const onScroll = () => {
        if (frame) return
        frame = requestAnimationFrame(() => {
          frame = 0
          revealInView()
        })
      }
      window.addEventListener('scroll', onScroll, { passive: true })
      window.addEventListener('resize', onScroll)
      // Safety net after load, as the reference does.
      const late = window.setTimeout(revealInView, 600)
      // Late mounts (streamed route content, a client component replacing
      // its node after that pass) re-run the frame-throttled reveal check, so
      // a reveal already on screen never waits for the next scroll. A late
      // headline's words are indexed here too; their reveal is CSS, so the
      // words show even if this observer never fires.
      // Only element additions can bring a headline or reveal target; the
      // count-up's per-frame text swaps add text nodes alone and are skipped.
      const mounts = new MutationObserver(records => {
        const mounted = records.some(r =>
          [...r.addedNodes].some(n => n.nodeType === Node.ELEMENT_NODE)
        )
        if (!mounted) return
        indexHeroWords()
        onScroll()
      })
      mounts.observe(document.body, { childList: true, subtree: true })
      cleanups.push(() => {
        window.removeEventListener('scroll', onScroll)
        window.removeEventListener('resize', onScroll)
        window.clearTimeout(late)
        mounts.disconnect()
        cancelAnimationFrame(frame)
      })
    }

    // Count-up. The served text is the final value; it only animates when
    // the element's text is exactly that formatted number.
    const fmt = new Intl.NumberFormat('en-US')
    const countIO = new IntersectionObserver(
      entries => {
        entries.forEach(e => {
          if (!e.isIntersecting) return
          countIO.unobserve(e.target)
          const el = e.target as HTMLElement
          const end = Number(el.dataset.count)
          if (
            !Number.isFinite(end) ||
            el.textContent?.trim() !== fmt.format(end)
          )
            return
          const dur = reducedMotion() ? 1 : 900
          const t0 = performance.now()
          const step = (t: number) => {
            const p = Math.min(1, (t - t0) / dur)
            const k = 1 - Math.pow(1 - p, 4)
            el.textContent = fmt.format(Math.round(end * k))
            if (p < 1) requestAnimationFrame(step)
          }
          requestAnimationFrame(step)
        })
      },
      { threshold: 0.6 }
    )
    document
      .querySelectorAll('.shell [data-count]')
      .forEach(el => countIO.observe(el))
    cleanups.push(() => countIO.disconnect())

    // Hive glyph: cells light in sequence.
    const cells = Array.from(
      document.querySelectorAll<SVGElement>(
        '.shell [data-hive-glyph] .cell, .shell #hiveGlyph .cell'
      )
    )
    if (cells.length) {
      let i = 0
      const timer = window.setInterval(() => {
        if (reducedMotion()) return
        cells.forEach(c => c.classList.remove('on'))
        ;[i, i + 2, i + 4].forEach(n =>
          cells[n % cells.length].classList.add('on')
        )
        i++
      }, 520)
      cleanups.push(() => window.clearInterval(timer))
    }

    // Hero engine art (reference #engineart): module focus with its hint,
    // pointer parallax, and the signal running only while it is on screen.
    const art = document.querySelector<HTMLElement>('.shell #engineart')
    if (art) {
      const hint = document.getElementById('engineHint')
      const names: Record<string, string> = {
        radar: 'Radar · See what AI says about your brand and what to fix.',
        vector: 'Vector · Score each lead. Know the next move.',
        hive: 'Hive · Coordinate AI co-workers. Keep people in control.',
      }
      const idleHint = hint?.textContent ?? ''
      const setFocus = (k: string | null) => {
        art.dataset.focus = k || 'all'
        if (hint) hint.textContent = k ? `${names[k]} →` : idleHint
      }
      art.querySelectorAll<SVGElement>('.hpe-module').forEach(m => {
        const on = () => setFocus(m.dataset.module ?? null)
        const off = () => setFocus(null)
        m.addEventListener('pointerenter', on)
        m.addEventListener('pointerleave', off)
        m.addEventListener('focus', on)
        m.addEventListener('blur', off)
        cleanups.push(() => {
          m.removeEventListener('pointerenter', on)
          m.removeEventListener('pointerleave', off)
          m.removeEventListener('focus', on)
          m.removeEventListener('blur', off)
        })
      })
      const scene = art.querySelector<SVGElement>('.hpe-engine-scene')
      const move = (e: PointerEvent) => {
        if (
          reducedMotion() ||
          !scene ||
          art.hasAttribute('data-scout-sequence') ||
          art.dataset.sequenceState === 'paused' ||
          art.dataset.sequenceState === 'reduced'
        )
          return
        const r = art.getBoundingClientRect()
        const x = (e.clientX - r.left) / r.width - 0.5
        const y = (e.clientY - r.top) / r.height - 0.5
        scene.style.setProperty('--hpe-pointer-x', `${(x * 14).toFixed(1)}px`)
        scene.style.setProperty('--hpe-pointer-y', `${(y * 10).toFixed(1)}px`)
      }
      const leave = () => {
        scene?.style.setProperty('--hpe-pointer-x', '0px')
        scene?.style.setProperty('--hpe-pointer-y', '0px')
      }
      art.addEventListener('pointermove', move)
      art.addEventListener('pointerleave', leave)
      const engIO = new IntersectionObserver(
        es =>
          es.forEach(e =>
            art.classList.toggle(
              'is-live',
              e.isIntersecting && !reducedMotion()
            )
          ),
        { threshold: 0.25 }
      )
      // Scout and the signal chain share their own pauseable clock.
      if (!art.hasAttribute('data-scout-sequence')) engIO.observe(art)
      cleanups.push(() => {
        engIO.disconnect()
        art.removeEventListener('pointermove', move)
        art.removeEventListener('pointerleave', leave)
      })
    }

    // Homepage film facade (reference home #yt): click-to-play, nocookie
    // embed. Other reference pages bind their own #yt in their page script.
    const yt = document.querySelector<HTMLElement>('.pg-home #yt')
    if (yt) {
      const play = () => {
        if (yt.dataset.on) return
        yt.dataset.on = '1'
        const f = document.createElement('iframe')
        f.src = `https://www.youtube-nocookie.com/embed/${yt.dataset.id}?autoplay=1&rel=0&iv_load_policy=3&playsinline=1`
        f.title =
          'What Is Decision-Stage AI Visibility? Lloyd & Scout Explain Radar'
        f.allow =
          'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
        f.allowFullscreen = true
        yt.replaceChildren(f)
        yt.style.cursor = 'default'
      }
      const key = (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          play()
        }
      }
      yt.addEventListener('click', play)
      yt.addEventListener('keydown', key)
      cleanups.push(() => {
        yt.removeEventListener('click', play)
        yt.removeEventListener('keydown', key)
      })
    }

    // Marquee (reference #marquee): the track is doubled for a seamless loop.
    const mq = document.querySelector<HTMLElement>('.shell #marquee')
    if (mq && !mq.dataset.doubled) {
      mq.dataset.doubled = '1'
      Array.from(mq.children).forEach(c => {
        const clone = c.cloneNode(true) as HTMLElement
        clone.setAttribute('aria-hidden', 'true')
        mq.appendChild(clone)
      })
    }

    // No magnetic buttons: CTAs keep a colour-only hover (Lloyd,
    // 2026-10-09).

    // Scroll-spy rail: one entry per [data-rail] section.
    const sections = Array.from(
      document.querySelectorAll<HTMLElement>('[data-rail][id]')
    )
    setRail(sections.map(s => ({ id: s.id, label: s.dataset.rail ?? '' })))
    setRailActive(-1)
    const spy = new IntersectionObserver(
      entries => {
        entries.forEach(e => {
          if (!e.isIntersecting) return
          const dark = e.target.classList.contains('sheet--ink')
          setRailActive(sections.indexOf(e.target as HTMLElement))
          setRailDark(dark)
          document.body.dataset.under = dark ? 'dark' : 'light'
        })
      },
      { rootMargin: '-45% 0px -50% 0px', threshold: 0 }
    )
    sections.forEach(s => spy.observe(s))
    cleanups.push(() => spy.disconnect())

    // Nav band: the header bar takes the token context of the sheet beneath
    // its bottom edge.
    const bar = document.querySelector<HTMLElement>('[data-header-bar]')
    const sheets = Array.from(
      document.querySelectorAll<HTMLElement>('.sheet--ink, .sheet--accent')
    )
    const navBand = () => {
      if (!bar) return
      const y = bar.getBoundingClientRect().bottom
      let band: 'dark' | 'accent' | null = null
      if (!document.getElementById('menu')?.classList.contains('is-open')) {
        for (const s of sheets) {
          const r = s.getBoundingClientRect()
          if (r.top <= y && r.bottom > y) {
            band = s.classList.contains('sheet--accent') ? 'accent' : 'dark'
            break
          }
        }
      }
      if (band) bar.dataset.band = band
      else delete bar.dataset.band
    }
    window.addEventListener('scroll', navBand, { passive: true })
    window.addEventListener('resize', navBand)
    navBand()
    cleanups.push(() => {
      window.removeEventListener('scroll', navBand)
      window.removeEventListener('resize', navBand)
      if (bar) delete bar.dataset.band
    })

    return () => cleanups.forEach(fn => fn())
  }, [pathname])

  if (!mounted) return null

  return (
    <div className='shell'>
      <div className='grain' aria-hidden='true' />
      {rail.length > 0 && (
        <nav
          className='rail'
          aria-label='Sections'
          data-on={railDark ? 'dark' : 'light'}
        >
          {rail.map((item, i) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              title={item.label}
              className={i === railActive ? 'is-active' : undefined}
            >
              <span>{String(i).padStart(2, '0')}</span>
            </a>
          ))}
        </nav>
      )}
    </div>
  )
}
